<script setup lang="ts">
import { InvoicesEmbed } from '@/Pejer/tables/Invoices'
import { Ban, ChevronDown, FileTextIcon } from 'lucide-vue-next'
import { Button } from 'pejer/src/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'pejer/src/components/ui/dropdown-menu'
import CancelInvoiceModal from '@/components/ClubAccount/CancelInvoiceModal.vue'
import ClubAccountActions from '@/components/ClubAccount/ClubAccountActions.vue'
import { ClubAccountItem, isUnpaid } from '@/components/ClubAccount/types'
import { computed } from 'vue'
import InitiateDebtCollectionButton from './InitiateDebtCollectionButton.vue'
import InvoiceRefundModal from './InvoiceRefundModal.vue'

const props = defineProps<{
  invoice: InvoicesEmbed.ListItem
}>()

// The invoices schema converts both grossAmount and kidNorwayInvoice.paidAmount to kroner.
const clubAccountItem = computed<ClubAccountItem>(() => ({
  paymentReferenceID: props.invoice.paymentReferenceID,
  description: props.invoice.description,
  paymentStatus: props.invoice.paymentStatus,
  customerName: props.invoice.customer?.name,
  kid: props.invoice.kidNorwayInvoice?.kid,
  grossKroner: props.invoice.grossAmount,
  paidKroner: props.invoice.kidNorwayInvoice?.paidAmount ?? 0,
}))

/**
 * Kravia-faktura via Dintero: beløp og KID er låst hos Kravia, betalte fakturaer kan ikke refunderes, og det er ingen inkasso.
 * Ubetalte kan bare kanselleres (refund-endepunktet kansellerer og annullerer fakturaen hos Kravia).
 */
const isDintero = computed(
  () => props.invoice.fundsLocation === 'Dintero' || props.invoice.paymentProvider === 'Dintero'
)

const isClubAccount = computed(
  () =>
    props.invoice.fundsLocation === 'ClubAccount' ||
    !!props.invoice.kidNorwayInvoice?.receiverIsDirectPaymentAccount
)
</script>
<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" class="h-8 hover:bg-slate-200">
        <span>Valg</span>
        <ChevronDown class="w-4 h-4 shrink-0 ml-1" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuLabel>Valg</DropdownMenuLabel>
      <template
        v-if="invoice.paymentGateway === 'checkout' || invoice.paymentGateway === 'managedInvoice'"
      >
        <DropdownMenuSeparator />
        <DropdownMenuGroup class="font-semibold">
          <DropdownMenuItem class="p-0 text-slate-700">
            <a class="w-full" :href="invoice.kidNorwayInvoice.invoiceUrl" target="_blank">
              <Button class="w-full gap-2 flex flex-nowrap text-left justify-start" variant="ghost">
                <FileTextIcon class="w-4 h-4" />Se Faktura
              </Button>
            </a>
          </DropdownMenuItem>
          <!-- <DropdownMenuItem class="p-0 text-slate-700 justify-start text-left">
            <SendReminderButton :invoice="invoice" />
          </DropdownMenuItem> -->
          <template
            v-if="
              (invoice.paymentStatus === 'InvoiceConfirmed' ||
                (invoice.paymentGateway === 'managedInvoice' &&
                  invoice.paymentStatus === 'InitiatedPayment')) &&
              (!invoice.kidNorwayInvoice.debtCollectionActivated ||
                !invoice.hasApprovedPersonalDebtCollectionAgreement) &&
              invoice.canActivatePersonalDebtCollection &&
              !isDintero
            "
          >
            <DropdownMenuSeparator />
            <DropdownMenuItem class="p-0 text-slate-700 justify-start">
              <InitiateDebtCollectionButton :invoice="invoice" />
            </DropdownMenuItem>
          </template>
        </DropdownMenuGroup>
      </template>
      <template v-if="isDintero">
        <template v-if="isUnpaid(clubAccountItem)">
          <DropdownMenuSeparator />
          <DropdownMenuItem class="p-0">
            <CancelInvoiceModal :item="clubAccountItem">
              <Button
                @click.prevent.stop
                class="w-full gap-2 flex flex-nowrap justify-start text-red-600"
                variant="ghost"
              >
                <Ban class="w-4 h-4" />Kanseller faktura
              </Button>
            </CancelInvoiceModal>
          </DropdownMenuItem>
        </template>
      </template>
      <ClubAccountActions v-else-if="isClubAccount" :item="clubAccountItem" />
      <template
        v-else-if="
          invoice.paymentStatus === 'InitiatedPayment' ||
          invoice.paymentStatus === 'InvoiceConfirmed' ||
          invoice.paymentStatus === 'ChargedPayment'
        "
      >
        <DropdownMenuSeparator />
        <DropdownMenuGroup class="font-semibold">
          <DropdownMenuItem class="p-0">
            <InvoiceRefundModal :invoice="invoice">
              <Button @click.prevent.stop class="w-full flex flex-col h-fit" variant="ghost">
                <!-- <span v-if="invoice.paymentGateway === 'checkout'" class="w-full text-left">
                  Krediter
                </span>
                <span v-else-if="invoice.kidNorwayInvoice.paidAmount > 0" class="w-full text-left">
                  Behandle
                </span>
                <span v-else class="w-full text-left"> Juster fakturabeløp </span> -->
                Behandle faktura
              </Button>
            </InvoiceRefundModal>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
